import { Link } from "react-router-dom";
import { ArrowLeft, Scroll } from "@phosphor-icons/react";

/**
 * ContestTerms — public T&Cs page for the Hunt-the-Needle contest.
 *
 * Linked from:
 *   • Browse-page contest banner
 *   • NeedleFound "You Found It" page (small text link under the blurb)
 *   • Admin contest editor (for reference)
 *
 * Written to cover the user-stated rules PLUS the standard sweepstakes
 * boilerplate that keeps us legally clean: no-purchase-necessary,
 * void-where-prohibited, sponsor identification, fraud disqualification,
 * modification rights.
 */
export default function ContestTerms() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-6" data-testid="contest-terms">
      <div>
        <Link to="/browse" data-testid="contest-terms-back" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white">
          <ArrowLeft size={12} weight="bold" /> Back to Browse
        </Link>
        <div className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">
          <Scroll size={14} weight="bold" /> Hunt the Needle Contest
        </div>
        <h1 className="mt-1 font-display font-black text-3xl">Contest Details &amp; Terms</h1>
        <p className="mt-1 text-xs text-zinc-500">Last updated: Feb 20, 2026</p>
      </div>

      <Section title="1. Sponsor">
        <p>The Hunt-the-Needle contest (the &ldquo;<b>Contest</b>&rdquo;) is sponsored by <b>Breaking Legends, LLC</b>, operator of Hunt57 (<a href="https://hunt57.com" className="text-[#FFD700] hover:underline">hunt57.com</a>).</p>
      </Section>

      <Section title="2. Eligibility">
        <ul className="list-disc pl-5 space-y-1">
          <li>Winners must be a <b>registered user of hunt57.com</b> in good standing at the time of winning.</li>
          <li>Members whose accounts have been suspended, banned, or are otherwise not in good standing are ineligible.</li>
          <li>Employees and immediate family of Breaking Legends, LLC are not eligible.</li>
          <li><b>Void where prohibited</b> by law. Contest is limited to jurisdictions where sweepstakes of this type are permitted.</li>
        </ul>
      </Section>

      <Section title="3. No Purchase Necessary">
        <p>No purchase, payment, or subscription is required to enter or win. A purchase or payment will not increase your chances of winning.</p>
      </Section>

      <Section title="4. How to Enter">
        <ul className="list-disc pl-5 space-y-1">
          <li>Each week during an active Contest, a small needle graphic (&ldquo;<b>Needle</b>&rdquo;) is hidden somewhere on a randomly chosen listing page on hunt57.com.</li>
          <li>Registered members who find and click the Needle before the weekly deadline are entered into the current week&apos;s drawing pool.</li>
          <li><b>Entries are limited to one (1) per member per week.</b> Attempting to circumvent this limit via duplicate accounts or automation will result in disqualification and possible account termination.</li>
          <li>Entries carry through the entire Contest — each week adds to your total odds in the final drawing.</li>
        </ul>
      </Section>

      <Section title="5. Weekly Deadlines">
        <p>Each weekly round closes automatically at <b>12:00 AM Central Time on Sunday</b>. Entries received after the deadline are not accepted. A new week may (or may not) be opened by the Sponsor later that Sunday.</p>
      </Section>

      <Section title="6. Grand Prize">
        <ul className="list-disc pl-5 space-y-1">
          <li>Only the <b>Grand Prize</b> has value. Its exact nature (e.g. a site gift certificate of a stated dollar amount) is described on the Contest banner and the &ldquo;You Found It&rdquo; page for each Contest and may vary between Contests.</li>
          <li>The Grand Prize is <b>redeemable only by a registered user of hunt57.com</b>, only <b>at hunt57.com</b>.</li>
          <li>The Grand Prize is <b>non-transferable</b>, has <b>no cash value</b>, and cannot be exchanged for cash or credited to any external account.</li>
          <li>The Grand Prize <b>expires 90 days after issuance</b> if unused. After expiration it is forfeited and cannot be reinstated.</li>
          <li>The Sponsor may substitute a Grand Prize of equal or greater value if the announced prize becomes unavailable.</li>
        </ul>
      </Section>

      <Section title="7. Drawing &amp; Winner Notification">
        <ul className="list-disc pl-5 space-y-1">
          <li>The winner is selected by the Sponsor at random from all valid entries across the Contest period, using an offline method chosen at the Sponsor&apos;s discretion.</li>
          <li>Odds of winning depend on the total number of valid entries received during the Contest.</li>
          <li>The winner will be notified via the email address on file for their hunt57.com account within a reasonable time after the Contest ends.</li>
          <li>The winner must claim their prize within <b>14 days</b> of notification. Unclaimed prizes may be forfeited and a runner-up selected at the Sponsor&apos;s discretion.</li>
        </ul>
      </Section>

      <Section title="8. Email Alerts (Optional)">
        <p>Members who opt in on the &ldquo;You Found It&rdquo; page will receive up to a few emails per Contest about needle drops and the final drawing — nothing else. Opt-in is per-Contest; a fresh opt-in is required each new Contest. You may opt out at any time by unchecking the box on the &ldquo;You Found It&rdquo; page or contacting support.</p>
      </Section>

      <Section title="9. Fraud &amp; Disqualification">
        <p>Any use of bots, scripts, multiple accounts, exploits, or other unfair means to enter the Contest will result in immediate disqualification and may result in account termination. The Sponsor reserves the right to void suspicious entries and to withhold prizes pending investigation.</p>
      </Section>

      <Section title="10. Publicity">
        <p>By entering, winners consent to the use of their hunt57.com username (never their email or legal name) in a low-key winner announcement on hunt57.com. Winners may opt out of the announcement by contacting support before claim.</p>
      </Section>

      <Section title="11. Privacy">
        <p>Entry data (username, email on file, timestamp) is used solely to administer the Contest and, for opted-in members, to send Contest alerts. Data is retained per our <Link to="/legal/privacy" className="text-[#FFD700] hover:underline">Privacy Policy</Link>. We do not sell entrant data.</p>
      </Section>

      <Section title="12. Modifications &amp; Termination">
        <p>The Sponsor reserves the right to modify, suspend, or terminate the Contest at any time and without prior notice if fraud, technical failure, or other factors beyond the Sponsor&apos;s reasonable control impair the integrity of the Contest.</p>
      </Section>

      <Section title="13. Disclaimers">
        <ul className="list-disc pl-5 space-y-1">
          <li>By entering, you release the Sponsor from any claims arising out of participation in the Contest or receipt/use of the Grand Prize.</li>
          <li>The Contest is not affiliated with, sponsored by, or endorsed by any card manufacturer, grading company (e.g. PSA, BGS, SGC), payment processor (e.g. PayPal, Cash App, Venmo, Stripe), or any listed seller on hunt57.com.</li>
          <li>Sponsor&apos;s decisions are final and binding on all matters relating to the Contest.</li>
        </ul>
      </Section>

      <Section title="14. Governing Law">
        <p>These terms are governed by the laws of the state in which Breaking Legends, LLC is organized, without regard to conflict-of-laws principles.</p>
      </Section>

      <Section title="15. Contact">
        <p>Questions about the Contest? Reach out through the <Link to="/contact" className="text-[#FFD700] hover:underline">Contact</Link> page.</p>
      </Section>

      <div className="pt-6 border-t border-white/10 flex justify-center">
        <Link to="/browse" className="h57-btn-secondary inline-flex items-center gap-2" data-testid="contest-terms-back-btn">
          <ArrowLeft size={14} weight="bold" /> Back to Browse
        </Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="h57-surface p-5">
      <h2 className="font-display font-bold text-sm uppercase tracking-widest text-white mb-2">{title}</h2>
      <div className="text-sm text-zinc-300 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
